import type { ModuleKey } from '@/access/modules';
import type { Database } from '@/lib/database.types';

/** רק טבלה שקיימת בסכימה. טעות כתיב ב registry תיתפס בזמן קומפילציה. */
export type TableName = keyof Database['public']['Tables'];

/**
 * רשימות הניהול (PRD 5.13).
 *
 * זו אינה "רשימה קשיחה" במובן של PRD 2.4: אין כאן אף ערך עסקי, רק תיאור
 * של אילו טבלאות ניתנות לעריכה ואילו עמודות יש בהן. הערכים עצמם תמיד
 * מגיעים מבסיס הנתונים, והמסך הגנרי נבנה מהתיאור הזה.
 */

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'color'
  | 'time'
  | 'select';

/** שמות העמודות של טבלה אחת, כפי שהן בסכימה. */
type ColumnOf<T extends TableName> = keyof Database['public']['Tables'][T]['Row'] & string;

export type FieldOf<T extends TableName> = {
  name: ColumnOf<T>;
  label: string;
  type: FieldType;
  required?: boolean;
  /** עזרה קצרה מתחת לשדה, כשהמשמעות לא מובנת מאליה. */
  hint?: string;
  /**
   * ל type: 'select' בלבד, ורק כשהאפשרויות הן ה enum של העמודה
   * בסכימה — כלומר רשימה שאי אפשר להוסיף לה ערך בלי מיגרציה.
   * רשימה עסקית היא תמיד טבלה עם מסך ניהול (כלל 1), ולכן היא
   * לא תגיע לכאן. fromSchemaEnum הוא ההצהרה, והבדיקה אוכפת אותה.
   */
  options?: { value: string; label: string }[];
  fromSchemaEnum?: true;
};

/**
 * לצרכני המסך, שלא אכפת להם מאיזו טבלה השדה בא. רופף בכוונה:
 * איחוד של `FieldOf` על כל הטבלאות היה מצמצם את `name` לחיתוך
 * העמודות המשותפות, כלומר ל `never`. ההקפדה חלה על ההגדרה
 * ב `LISTS`, והקריאה ממנה היא `string`.
 */
export type Field = {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  hint?: string;
  options?: { value: string; label: string }[];
  fromSchemaEnum?: true;
};

type ListDefFor<T extends TableName> = {
  /** המפתח בנתיב: /admin/settings/<key> */
  key: string;
  table: T;
  /**
   * שם עמודת המפתח הראשי, אם אינה `id`. `notification_categories`
   * ננעלת על `key` טקסטואלי ואין בה `id` בכלל, והמסך הגנרי היה
   * מחזיר 400 על בקשת עמודה שאינה קיימת.
   */
  pk?: ColumnOf<T>;
  label: string;
  description: string;
  /** מפתח ההרשאה שנדרש כדי לערוך. */
  perm: string;
  /** מוצג רק כשהמודול פעיל. undefined = ליבה. */
  module?: ModuleKey;
  fields: FieldOf<T>[];
  /** לטבלה יש sort_order, ולכן אפשר לשנות סדר. */
  /**
   * רשימה שיש לה בית טבעי בטאב שלה, ולכן אינה מופיעה גם
   * במסך ההגדרות. שני מקומות לאותו דבר הם שני מקומות לחפש בהם.
   */
  hideFromSettings?: boolean;
  sortable?: boolean;
  /** לטבלה יש is_active, ולכן מחיקה היא השבתה (PRD 2.3). */
  deactivatable?: boolean;
};

/**
 * האיחוד על כל הטבלאות הוא מה שהופך את ה registry לנבדק בזמן קומפילציה:
 * `pk` ושם של כל שדה חייבים להיות עמודה אמיתית *באותה* טבלה. עמודה
 * שאינה קיימת נתפסת ב `tsc`, ולא כשגיאת 400 במסך.
 */
export type ListDef = { [T in TableName]: ListDefFor<T> }[TableName];

/** `as const` ולא `: Field`: ליטרל 'name' מתאים לכל טבלה שיש בה העמודה. */
const NAME = { name: 'name', label: 'שם', type: 'text', required: true } as const;

export const LISTS: ListDef[] = [
  {
    key: 'branches',
    table: 'branches',
    label: 'סניפים',
    description: 'הסניפים ברשת. הוספת סניף לא דורשת שינוי קוד.',
    perm: 'admin.settings',
    sortable: true,
    deactivatable: true,
    fields: [
      NAME,
      { name: 'address', label: 'כתובת', type: 'text' },
      { name: 'phone', label: 'טלפון', type: 'text' },
      { name: 'color', label: 'צבע', type: 'color' },
      {
        name: 'comax_branch_code',
        label: 'קוד בקופה',
        type: 'text',
        hint: 'לסנכרון מכירות, אם יש חיבור למערכת הקופות',
      },
    ],
  },
  {
    key: 'job-titles',
    table: 'job_titles',
    label: 'תפקידים',
    description:
      'התפקיד שמוצג בכרטיס העובד, והתקרה שהוא מציב לתפקיד ההרשאות שאפשר לתת לעובד בו.',
    perm: 'admin.settings',
    sortable: true,
    deactivatable: true,
    fields: [
      NAME,
      { name: 'color', label: 'צבע', type: 'color' },
      {
        // מיגרציה 091. בלי זה אפשר לתת למוכר הרשאות של הנהלה
        // בלחיצה אחת ברשימה הסמוכה.
        name: 'max_role_scope',
        label: 'תקרת הרשאות',
        type: 'select',
        required: true,
        // ה check על roles.scope. הוספת היקף רביעי היא מיגרציה,
        // ולכן זו אינה רשימה עסקית שצריכה מסך ניהול.
        fromSchemaEnum: true,
        hint: 'ההיקף הרחב ביותר שאפשר לתת לעובד בתפקיד הזה. מנהל סניף אפשרי גם בתפקיד רצפה.',
        options: [
          { value: 'self', label: 'עצמי בלבד' },
          { value: 'own_branch', label: 'עד רמת סניף' },
          { value: 'all_branches', label: 'כל הרשת' },
        ],
      },
    ],
  },
  {
    key: 'shift-types',
    table: 'shift_types',
    label: 'סוגי משמרות',
    description: 'משמרות וסוגיהן. סניף ריק משמעו שהמשמרת קיימת בכל הסניפים.',
    perm: 'admin.settings',
    module: 'schedule',
    sortable: true,
    deactivatable: true,
    fields: [
      NAME,
      { name: 'start_time', label: 'שעת התחלה', type: 'time', required: true },
      { name: 'end_time', label: 'שעת סיום', type: 'time', required: true },
      { name: 'color', label: 'צבע', type: 'color' },
    ],
  },
  {
    // ניסוחים שחוזרים, כדי שלא יוקלדו מחדש בכל משמרת. התבנית
    // אינה מחליפה כתיבה חופשית אלא נוספת לה.
    key: 'task-templates',
    table: 'task_templates',
    label: 'תבניות משימה',
    description: 'משימות שחוזרות, לבחירה בלחיצה מתוך המשמרת. בלי סניף או תפקיד — מוצעת לכולם.',
    perm: 'admin.settings',
    module: 'ops',
    hideFromSettings: true,
    sortable: true,
    deactivatable: true,
    fields: [
      { name: 'title', label: 'המשימה', type: 'text', required: true },
      { name: 'description', label: 'פירוט', type: 'textarea' },
      {
        name: 'requires_photo',
        label: 'דורשת צילום',
        type: 'boolean',
        hint: 'העובד יתבקש לצרף תמונה כדי לסמן שבוצעה',
      },
    ],
  },
  {
    key: 'constraint-types',
    table: 'constraint_types',
    label: 'סוגי אילוצים',
    description: 'מה העובד יכול לסמן בטופס האילוצים, והאם זה חוסם שיבוץ.',
    perm: 'admin.settings',
    module: 'schedule',
    sortable: true,
    fields: [
      NAME,
      {
        name: 'is_blocking',
        label: 'חוסם שיבוץ',
        type: 'boolean',
        hint: 'סימון כזה ימנע מה AI לשבץ, ויציג אזהרה בבנייה הידנית',
      },
      {
        name: 'weight',
        label: 'משקל',
        type: 'number',
        hint: 'כמה חזק להתחשב בהעדפה. גבוה יותר, חשוב יותר',
      },
      { name: 'color', label: 'צבע', type: 'color' },
    ],
  },
  {
    key: 'course-categories',
    table: 'course_categories',
    label: 'קטגוריות לומדות',
    description: 'לפיהן מסודרות הלומדות במסך הלומדות.',
    perm: 'learning.manage',
    module: 'learning',
    sortable: true,
    fields: [NAME],
  },
  // מחלקות המוצרים אינן כאן. יש להן מסך משלהן בטאב מוצרים, כי שם
  // גם משייכים אליהן מוצרים — ושני מסכים שמנהלים אותה טבלה הם שני
  // מקומות לחפש בהם את אותה הגדרה.
  {
    key: 'doc-categories',
    table: 'doc_categories',
    label: 'קטגוריות נהלים',
    description: 'לפיהן מחולק מסך הנהלים: חנות, מחסן, משרד וכל מה שתוסיפו.',
    // docs.manage ולא admin.settings: זו ההרשאה שמדיניות ה RLS על
    // הטבלה דורשת, ומי שכותב נהלים הוא מי שמגלה שחסרה לו קטגוריה
    perm: 'docs.manage',
    module: 'ops_documents',
    sortable: true,
    fields: [NAME],
  },
  {
    key: 'feedback-categories',
    table: 'feedback_categories',
    label: 'קטגוריות משוב',
    description: 'מה העובד יכול לבחור כשהוא שולח משוב.',
    perm: 'admin.settings',
    module: 'ops',
    fields: [NAME],
  },
  {
    key: 'hr-request-types',
    table: 'hr_request_types',
    label: 'סוגי בקשות',
    description: 'חופשה, מחלה וכל סוג אחר שתרצו להוסיף.',
    perm: 'hr.manage',
    module: 'ops',
    fields: [
      NAME,
      { name: 'requires_attachment', label: 'דורש צירוף קובץ', type: 'boolean' },
      {
        name: 'is_sensitive',
        label: 'מידע רגיש',
        type: 'boolean',
        hint: 'הקובץ יהיה גלוי רק לעובד ולמנהל עם הרשאת כוח אדם',
      },
    ],
  },
  {
    key: 'task-statuses',
    table: 'task_statuses',
    label: 'סטטוסים של משימות',
    description: 'השלבים שמשימה עוברת.',
    perm: 'admin.settings',
    module: 'ops',
    sortable: true,
    fields: [
      NAME,
      { name: 'is_done_state', label: 'נחשב כהושלם', type: 'boolean' },
      { name: 'color', label: 'צבע', type: 'color' },
    ],
  },
  {
    key: 'task-priorities',
    table: 'task_priorities',
    label: 'עדיפויות של משימות',
    description: 'רמות הדחיפות של משימה.',
    perm: 'admin.settings',
    module: 'ops',
    sortable: true,
    fields: [NAME, { name: 'color', label: 'צבע', type: 'color' }],
  },
  {
    key: 'point-rules',
    table: 'point_rules',
    label: 'כללי נקודות',
    description: 'כמה נקודות מקבלים על כל פעולה. כיבוי כלל מפסיק לזכות בו.',
    perm: 'admin.settings',
    module: 'games',
    deactivatable: true,
    fields: [
      { name: 'label', label: 'שם הפעולה', type: 'text', required: true },
      { name: 'points', label: 'נקודות', type: 'number', required: true },
      {
        name: 'action_key',
        label: 'מפתח',
        type: 'text',
        hint: 'הקוד מזהה את הכלל לפי המפתח. שינוי כאן מנתק אותו.',
      },
    ],
  },
  {
    key: 'sms-templates',
    table: 'sms_templates',
    label: 'תבניות SMS',
    description: 'נוסח ההודעות שיוצאות מהמערכת. שינוי כאן משפיע על ההודעה הבאה.',
    perm: 'admin.settings',
    fields: [
      NAME,
      {
        name: 'body',
        label: 'נוסח ההודעה',
        type: 'textarea',
        required: true,
        hint: 'אפשר לשלב {first_name}, {code}, {app_url} ו {deadline}.',
      },
      {
        name: 'key',
        label: 'מפתח',
        type: 'text',
        hint: 'הקוד מזהה את התבנית לפי המפתח. שינוי כאן מנתק אותה.',
      },
    ],
  },
  {
    key: 'notification-categories',
    table: 'notification_categories',
    pk: 'key',
    label: 'סוגי התראות',
    description: 'מה העובד יכול לכבות, ומה תמיד מגיע אליו.',
    perm: 'admin.settings',
    sortable: true,
    fields: [
      NAME,
      {
        name: 'is_mandatory',
        label: 'התראת חובה',
        type: 'boolean',
        hint: 'התראת חובה מגיעה גם למי שכיבה את הסוג הזה קודם.',
      },
      { name: 'default_enabled', label: 'דלוק כברירת מחדל', type: 'boolean' },
      {
        name: 'key',
        label: 'מפתח',
        type: 'text',
        hint: 'הקוד שולח לפי המפתח. שינוי כאן מנתק את ההתראה.',
      },
    ],
  },
  {
    key: 'award-types',
    table: 'award_types',
    label: 'סוגי מצטיינים',
    description: 'עובד החודש, סניף החודש וכל פרס אחר.',
    perm: 'admin.settings',
    module: 'games',
    fields: [
      NAME,
      { name: 'default_points', label: 'נקודות', type: 'number' },
      { name: 'icon', label: 'אייקון', type: 'text', hint: 'שם אייקון מתוך Lucide' },
    ],
  },
];

export const findList = (key: string | undefined) => LISTS.find((list) => list.key === key);
